import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AppStoreBadges } from '../../../components/layout/AppStoreBadges'
import { buttonClasses } from '../../../components/ui/button-variants'
import { Container } from '../../../components/ui/Container'
import { Rating } from '../../../components/ui/Rating'
import { Skeleton } from '../../../components/ui/Skeleton'
import { useLocaleSegment } from '../../../hooks/useLocale'
import { showAppSurfaces } from '../../../lib/appSurfaces'
import { DEFAULT_CITY } from '../../../lib/cities'
/*
 * ✅ `DEFAULT_CITY`, `directoryPath` and `useLocaleSegment` stay imported in
 * BOTH states now, because the links are branched rather than deleted. They
 * used to need removing by hand on a hide and re-adding on a restore — Docs/34
 * § 1b.2 row 8 counted sixteen such symbols across four files. That bookkeeping
 * is gone in both directions.
 */
import { directoryPath, routes } from '../../../lib/navigation'
import { usePlatformStats } from '../hooks/usePlatformStats'
import { AppShot } from './AppShot'

/** The two floating cards, keyed off the `home.hero.trust.*` branch. */
const CARDS = ['availability', 'offers'] as const

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE EXAMPLE CATEGORIES — "ALMOST EVERYTHING LOCAL, IN ONE PLACE"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The client's 29 Sep review (§ 6) names this as the single strongest message
 * the homepage has: *"One of the strongest messages should be that users can
 * find almost everything locally in one place. For example: Electricians.
 * Lawyers. Car mechanics. Restaurants. Other local businesses and services."*
 *
 * A sentence cannot carry that, because the point is the RANGE — a trade, a
 * profession, a workshop and a restaurant have nothing in common except being
 * local, and seeing those four side by side is the argument. So they are a
 * visible row rather than a clause in the subtitle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ✅ THEY ARE LINKS AGAIN — 7 OCTOBER 2026, Docs/34 § 1b
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * For about six hours that day they were plain `<span>`s: the directory was
 * hidden, so the row kept the client's § 6 message and lost its funnel. The
 * commercial position changed to showing the directory with a visible notice
 * (`components/layout/PreviewNotice.tsx`), so the six pills are `<Link>`s into
 * `/:locale/:city/:category` again and the seventh "All categories →" pill is
 * back.
 *
 * ⚠️ IF THEY ARE EVER DE-LINKED AGAIN, DO NOT LEAVE THEM LOOKING CLICKABLE.
 * The row itself must STAY either way — the client asked in § 6 for the
 * strongest message on this page to be that you find almost everything locally
 * in one place, and gave these four trades as his example; a trade, a
 * profession, a workshop and a restaurant seen side by side IS that argument,
 * and it does not need a destination to be true. But the hover border, the
 * `hover:bg-card` and `touch-target` all have to go with the links, so the
 * pills read as `SectionSplit`'s chips (which have never been links) rather
 * than as buttons that do nothing. A pill that lifts under the pointer and then
 * goes nowhere is a worse page than either version.
 *
 * ⚠️ THE SLUGS: EVERY ONE HERE EXISTS IN `supabase/seed/01-categories.mjs`, and
 * that is not a coincidence — check it if you change one. `elektriker`,
 * `advokat`, `bilverksted` and `restaurant` are the client's own four and were
 * confirmed present; `rorlegger` and `rengjoring` were added to show the range
 * is wider than his four. A typo does not throw — it renders a directory page
 * with no businesses on it, which is the failure that survives review.
 *
 * ⚠️ THE CITY IS `DEFAULT_CITY`, AND IT IS A GUESS WE ARE ALLOWED TO MAKE: the
 * directory route needs a city segment, the homepage does not know where the
 * visitor is, there is no geolocation and no `cities` table to resolve one
 * against, and the destination page carries its own city switcher — so the cost
 * of being wrong is one click. Do NOT add a geolocation prompt to fix it; that
 * is a consent decision, not a layout one.
 *
 * The LABELS go through i18n (`home.hero.categories.*`) and the SLUGS do not —
 * a slug is a URL, not a display string, and translating one would break every
 * link anybody has shared.
 */
const HERO_CATEGORIES = [
  { slug: 'elektriker', labelKey: 'home.hero.categories.electrician' },
  { slug: 'advokat', labelKey: 'home.hero.categories.lawyer' },
  { slug: 'bilverksted', labelKey: 'home.hero.categories.carRepair' },
  { slug: 'restaurant', labelKey: 'home.hero.categories.restaurant' },
  { slug: 'rorlegger', labelKey: 'home.hero.categories.plumber' },
  { slug: 'rengjoring', labelKey: 'home.hero.categories.cleaning' },
] as const

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE HERO, BUILT TO THE CLIENT'S OWN RENDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Docs/From Client/Nettside - Example.pdf`, page 1, as rendered rather than as
 * decoded — the developer supplied the image, so this is their layout and not
 * a reading of extracted text.
 *
 *   LEFT   eyebrow · a two-colour headline · the body line · a row of example
 *          categories · two CTAs · "Coming to the App Store · Google Play" ·
 *          stars and a tagline
 *   RIGHT  two overlapping phone screens in a soft panel, with two floating
 *          cards over them
 *
 * ─── REWRITTEN 6 OCT TO THE CLIENT'S § 6, AND WHAT CHANGED ─────────────────
 *
 * He asked for it to be *"considerably more attractive and immediately
 * communicate why the platform is useful"*, and offered
 * *"The #1 Future App for Local Services"* as an example he explicitly invited
 * us to improve on. Three decisions came out of that:
 *
 *   • THE HEADLINE IS THE RANGE, NOT A SUPERLATIVE. "Everything local. One
 *     app." plus the four trades he named. ⚠️ "#1" IS DELIBERATELY NOT USED,
 *     and this is the same rule that keeps invented figures off this page: it
 *     is a market-position claim about a product that has not launched, nobody
 *     has measured it, and under the Norwegian Marketing Control Act
 *     (markedsføringsloven § 26) an unsubstantiated superlative is the one kind
 *     of marketing copy that can actually be ordered down. "Future app" has the
 *     same problem in a softer form. The range is a claim we can stand behind.
 *   • THE EXAMPLE CATEGORIES ARE A VISIBLE ROW OF LINKS, not a clause. See
 *     `HERO_CATEGORIES`.
 *   • THE THREE AUDIENCES HE LISTED — businesses, people who need something
 *     done, people who want to browse — are NOT in the hero. They are the band
 *     immediately below it (`AudienceStrip`), because all three with their
 *     bullets is roughly a screen of text on its own and putting it here would
 *     push the CTAs and the store badges below the fold on a laptop, which is
 *     the opposite of what he asked for.
 *
 * ⚠️ HE ALSO POINTED AT `Voximate.no` AS A REFERENCE AND LIKED THAT IT HAS A
 * VIDEO. There is no video. Nobody has shot one, there is no hosting decision
 * and an autoplaying hero video is 1–3 MB on the critical path of a page with a
 * 180 kB script budget. Not built, and not faked with a looping screen
 * recording — see the report accompanying this change.
 *
 * ─── 12 OCT: THE COPY CHANGED, THE STRUCTURE DID NOT, AND THAT IS A DECISION ─
 *
 * A re-read of §§ 5.1, 5.2 and 6 against this file found one real gap: the
 * hero said what the platform COVERS and never what it DOES. `home.hero.title`
 * plus `HERO_CATEGORIES` answer § 6's *"everything locally in one place"*, and
 * the old subtitle then repeated the same four trades a single line above the
 * pill row that lists six of them. So the words were being spent twice on the
 * message that was already landing, and **neither BIDDING nor SMALL JOBS — the
 * two things §§ 5.1 and 5.2 single out as under-presented — appeared anywhere
 * in the first screen.**
 *
 * Three strings changed and nothing else:
 *
 *   `home.hero.eyebrow`      was "One app for everything local", a third
 *                            statement of the `<h1>`. It now names the span of
 *                            the product rather than its breadth again.
 *   `home.hero.subtitle`     DROPPED the trade list (the pills below are the
 *                            trade list) and gained the three things a visitor
 *                            can actually do, bidding and small jobs among
 *                            them. It is SHORTER than what it replaced —
 *                            196 characters against 205 in `en`, 208 against
 *                            221 in `nb` — which is what makes this free.
 *   `home.hero.trust.offers` the second floating card was "Compare price,
 *                            timing and trust" and is now "Compare side by
 *                            side, or let them bid". It sits over the REQUESTS
 *                            screenshot, and bidding is a mode of a request
 *                            rather than a separate product, so it is the one
 *                            card where the word belongs.
 *
 * ⚠️ WHY NOTHING WAS ADDED, WHICH IS THE HARDER HALF OF THE DECISION. Three
 * things were considered and rejected:
 *
 *   • THE SEARCH BOX, which is the most literal reading of § 7's Voximate
 *     reference — *"a lot of important things was included on the top, very
 *     user-friendly"*, said about a hero. It is `DualSearch`, it lives in
 *     `TwoWaysSection` (band 4), and its own header records that it is the
 *     site's ONLY acquisition path into the directory Docs/07 § 2 calls the
 *     commercial heart of the site.
 *
 *     🔴 IT WAS NOT REJECTED ON SPACE, AND THE NUMBER BELOW IS WHY THAT MATTERS.
 *     It was rejected because **there is no arrangement where the hero gains
 *     the search and band 4 keeps its payload** — the page gets either two
 *     search boxes or a `TwoWaysSection` whose FIND half is a heading about
 *     searching with nothing to search, which is the exact state Docs/34 § 1a
 *     says is worse than either version. Band 4 is also a surface in a live
 *     commercial negotiation (Docs/34 § 1b): it was removed and restored inside
 *     one day on 7 Oct and is named as the first thing to turn back on. **So
 *     this is one decision about two bands, and it is the developer's**, not a
 *     copy pass's. It is cheap when he wants it: lucide, `Button` and
 *     `useCategoryTree` are all on the first load already, so the bundle cost
 *     is the component itself.
 *
 *   • THE THREE AUDIENCE CARDS. Already measured and rejected on 6 Oct, above,
 *     and `AudienceStrip` is one scroll away. Repeating it 400 px higher is the
 *     repetition his *"too long"* was about.
 *   • A VIDEO. Still nothing to play.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ✅ AND THE "TUNED TO THE PIXEL" WARNING NOW HAS A MEASUREMENT BEHIND IT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * That phrase has been repeated in this file and in globals.css for weeks with
 * no number attached, and it has been the stated reason for not adding anything
 * here. Measured on 12 Oct, in headless Chrome against the real build, both
 * locales (they were identical to the pixel — `nb` is the longer language but
 * `text-balance` and the fluid scale absorb it):
 *
 *   viewport    hero height   content ends at   verdict
 *   1024 × 768      748              605        whole hero fits
 *   1366 × 768      727              612        whole hero fits
 *   1280 × 800      745              634        whole hero fits
 *   1440 × 900      816              713        whole hero fits
 *
 * The primary CTA's bottom edge is at **575** of 768 at the tightest size. The
 * section is `min-height` + `justify-center`, so the content block floats in
 * the middle: at 1024 × 768 it occupies roughly 110 → 605 inside a 748 px
 * section, which leaves on the order of **120 px of room before the hero stops
 * fitting in a 768 px viewport at all.**
 *
 * ⚠️ SO THE RULE IS NOT "NOTHING MAY BE ADDED" — IT IS "MEASURE IT, AND
 * 1024 × 768 IS THE SIZE THAT DECIDES." A row costing 56 px (one
 * `touch-target` plus its margin) still fits; two of them do not. Re-run the
 * measurement rather than trusting this table, because every row of it is a
 * fact about one build.
 *
 * ─── THINGS IN THEIR RENDER THAT ARE DELIBERATELY NOT HERE ─────────────────
 *
 * ⚠️ THE "Thanks — your interest has been registered" CARD. It was captured by
 * accident while the example was being generated and is not part of the design,
 * confirmed by the developer. It is not drawn, and there is no confirmation
 * state built to justify it. A success message that is always on screen is a
 * lie about something that never happened.
 *
 * ⚠️ THE "3" IN "3 relevant offers". Three is a number nobody measured, on a
 * page that once printed 12 480 businesses and a 4.7 average out of
 * `lib/mockData.ts` — live on the client's marketing site — when the real
 * figures were 10 and 4.64. The card says "Relevant offers".
 *
 * ⚠️ THE FIVE PAINTED STARS. Five stars beside a sentence about trust is a
 * rating claim. The marks are `platform_stats()`'s real average over the real
 * review count, rendered by the same `Rating` the rest of the site uses — so a
 * platform with no reviews says "not rated yet" and can never render `0,0`. On
 * error the whole line is dropped and the tagline stands alone. There is no
 * fallback value and there may never be: a default is how the invented figures
 * got here in the first place.
 *
 * ─── "GET EARLY ACCESS" COLLECTS NOTHING, ON PURPOSE ───────────────────────
 *
 * Their render shows an email capture. There is no table for the addresses, no
 * decision about who may read them and no consent text — and under the GDPR
 * that is a decision, not a form (CLAUDE.md hard rule 4). So the button is
 * drawn as designed and points at `/download`, the page whose entire job is
 * "the app is not out yet, here is what is coming". `Docs/15` N28 asks the
 * question. **Do not add an input here until it is answered.**
 *
 * The second CTA reads "See how it works" and goes to `/how-it-works`, a real
 * page — which is also now the first item in the header. Their render draws a
 * ↓ on it, implying a scroll to § 2; that section lives in the lazy chunk, so a
 * hash anchor could fire before it exists, and Lenis fights programmatic
 * scrolling (Docs/06 § 8). A real page and a → is the honest version.
 *
 * ─── THIS IS THE ONLY EAGER SECTION ON THE PAGE ────────────────────────────
 *
 * Everything below the fold is one `lazy()` chunk (`HomeSections.tsx`), because
 * `/` is the entry route and the JS budget is 180 kB. Keep this file's import
 * list short — every module reached from here is downloaded by every visitor
 * before anything renders. In particular: no `lib/icons.ts`, no `Card`.
 *
 * ─── IT FILLS THE SCREEN FROM 768 px UP, AND IT CANNOT CLIP ────────────────
 *
 * `.hero-viewport` (globals.css) is `min-height: calc(100svh - var(--header-h))`
 * with a `100vh` fallback underneath it, and the rule is scoped to
 * `min-width: 768px`. Read that file's comment before changing any of it —
 * in particular WHY it is `svh` rather than `vh` or `dvh`, and why it is a
 * MINIMUM. The three decisions that live here rather than there:
 *
 *   • the section is a COLUMN FLEX with `justify-center`, so the content is
 *     optically centred in the screen while it is shorter than one, and simply
 *     grows the section and scrolls when it is not. A short laptop, a 200 %
 *     browser zoom and a phone in landscape all take the second path, and the
 *     rating line and both CTAs stay reachable in every one of them;
 *   • the padding came DOWN (`lg:py-20` → `lg:py-16`). Padding is a floor once
 *     a minimum height is doing the vertical work, and at 1024 × 768 the old
 *     value was the difference between fitting and not;
 *   • the chevron at the bottom is the affordance that the page continues.
 *     It is `md`-and-up because below that the hero is not full-height, so
 *     there is nothing to explain. It is drawn in CSS (`.scroll-cue`) — an
 *     icon import here would be paid for by every visitor on every route.
 *
 * ─── AND THE CONTENT GROWS INTO THAT HEIGHT ───────────────────────────────
 *
 * A full screen with the old content centred in it looked emptier than the
 * short hero it replaced, so `.hero-fluid` declares ONE custom property,
 * `--hero-grow` — a length from 0rem to 1rem, driven by `100svh` — and every
 * `hero-*` class here multiplies it by its own factor. The headline grows
 * most, then the mockups, then the lede, the buttons, the floating cards and
 * the vertical rhythm, so the block keeps its relationships.
 *
 * ⚠️ IT IS ZERO BELOW 1024 px AND AT 768 px OF HEIGHT. A phone gets exactly
 * the sizes it had before this change, and so does a short laptop — which is
 * what stops "bigger on a monitor" from becoming "clipped on a laptop". The
 * numbers and the reasoning are in globals.css under THE HERO'S CONTENT
 * GROWS INTO THE HEIGHT IT WAS GIVEN; read it before retuning any factor.
 */
export const HeroSection = () => {
  const { t } = useTranslation()
  const localeSegment = useLocaleSegment()
  const stats = usePlatformStats()

  return (
    <section className="hero-fluid hero-viewport relative isolate flex flex-col justify-center overflow-hidden border-b border-border">
      <div
        aria-hidden="true"
        className="mesh pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-info-subtle via-background to-background"
      />

      <Container className="w-full py-12 lg:py-16">
        {/* Capped below the 1440 px container: two halves of a full-width
            container put a lot of nothing between the headline and the
            screens on a wide desktop. */}
        {/* ⚠️ NO `lg:grid-cols-[…]` UTILITY HERE. The column tracks are set by
            `.hero-grid` in globals.css, because the mockup track has to grow
            with `--hero-grow`. Tailwind's utilities layer comes AFTER the
            components layer, so any `grid-cols-*` class added here would win
            and silently freeze that track back at 30rem. */}
        <div className="hero-grid mx-auto grid items-center gap-12 lg:gap-10 xl:gap-16">
          {/* ── The offer ───────────────────────────────────────────────── */}
          <div className="enter mx-auto w-full max-w-2xl text-center lg:mx-0 lg:max-w-none lg:text-left">
            <p className="type-overline text-info-subtle-foreground">{t('home.hero.eyebrow')}</p>

            {/* Two colours, one heading. Both halves are inside the `<h1>` so
                the document still has exactly one heading here — splitting the
                sentence across two elements for a colour change is a styling
                decision and must not become a structural one. */}
            <h1 className="hero-mt-tight hero-title type-display text-balance">
              <span className="block text-foreground">{t('home.hero.title')}</span>
              <span className="block text-primary">{t('home.hero.titleAccent')}</span>
            </h1>

            <p className="hero-lede hero-mt-mid type-body-lg text-pretty text-muted-foreground">
              {t('home.hero.subtitle')}
            </p>

            {/*
              ── THE RANGE, AS SIX LINKS AND A SEVENTH PILL ───────────────────

              ✅ LINKS AGAIN SINCE 7 OCT 2026 (Docs/34 § 1b). They were plain
              `<span>`s for about six hours that day while the directory was
              hidden; the reasoning in both directions is at `HERO_CATEGORIES`
              above.

              Three things about the markup:

              • it is a `<ul>` of `<Link>`s, and the list semantics matter in
                their own right — this IS a list of examples, and a screen
                reader saying "list, seven items" is part of the argument;
              • `touch-target` carries the 44 px minimum, because these are tap
                targets again. ⚠️ IT GOES BACK OUT WITH THE LINKS if they are
                ever de-linked — along with the hover border and `hover:bg-card`
                — or the pills promise an interaction that is not there;
              • `justify-center lg:justify-start`, matching the headline's own
                alignment switch, and it WRAPS rather than scrolling — a
                horizontal scroller here would hide half the argument behind a
                gesture, and the argument is the range.

              ⚠️ `hero-mt-tight`, NOT `hero-mt-mid`, AND THE REASON IS VERTICAL
              BUDGET. This row costs the hero about 56 px (a 44 px
              `touch-target` plus its margin). At 1024 × 768 the hero is tuned to
              the pixel and globals.css records that `lg:py-20` had to come down
              to `lg:py-16` to fit at that size, so the step stays tight rather
              than being spent: the hero is `min-height` with `justify-center`,
              overflowing makes the section scroll rather than clipping, and the
              thing that goes under the fold first is the primary CTA. If this
              row ever grows, MEASURE AT 1024 × 768 before shipping it.
            */}
            {/*
              🔴 LINKS OR TAGS, AND IT IS NOT JUST `<Link>` → `<span>` —
              ASYMMETRY #7 FROM Docs/34 § 1b.2.

              `touch-target`, `hover:border-primary/40`, `hover:bg-card` and
              `hover:text-foreground` ALL GO WITH THE LINK, **and so does the
              seventh "All categories →" pill.** A pill that lifts under the
              pointer and then does nothing is worse than either version, and
              this row is the one place on the page where somebody could
              reasonably say "those look clickable". The hidden shape is the
              same chip `SectionSplit` uses, so it reads as a tag.

              ⚠️ IF IT EVER READS WRONG, MAKE THEM PLAINER — NEVER RE-LINK THEM.
              The row is CONTENT either way: the client's § 6 calls *"you find
              almost everything locally in one place"* the page's strongest
              message and gave these trades as his own example. A trade, a
              profession, a workshop and a restaurant side by side **is** that
              argument, and it does not need a destination to be true.
            */}
            <ul className="hero-mt-tight flex flex-wrap justify-center gap-2 lg:justify-start">
              {HERO_CATEGORIES.map((category) =>
                showAppSurfaces() ? (
                  <li key={category.slug}>
                    <Link
                      to={directoryPath(localeSegment, DEFAULT_CITY.slug, category.slug)}
                      className="touch-target inline-flex items-center rounded-full border border-border bg-card/80 px-3.5 type-body-sm font-medium text-muted-foreground transition-colors duration-hover ease-out hover:border-primary/40 hover:bg-card hover:text-foreground"
                    >
                      {t(category.labelKey)}
                    </Link>
                  </li>
                ) : (
                  /*
                    ⚠️ PLAINER SINCE 13 OCT, FOLLOWING THIS FILE'S OWN
                    INSTRUCTION RATHER THAN OVERRIDING IT.

                    A QA pass reported these still *"look like buttons and do
                    nothing"*. The de-linking was already correct — no
                    `touch-target`, no hover border, no `hover:bg-card`, and the
                    comment above records why. What still read as a control was
                    the RESTING shape: `border border-border` plus `bg-card/80`
                    is an outlined pill, and an outlined pill is a button to most
                    people whether or not it reacts to a pointer.

                    So the border and the raised card fill are gone and the chip
                    sits on `bg-muted`. The row is unchanged as CONTENT, which is
                    what the note above insists on: the client's § 6 calls *"you
                    find almost everything locally in one place"* this page's
                    strongest message and gave these trades as his example.

                    🔴 STILL: NEVER RE-LINK THEM TO FIX THIS. The destinations
                    are app-backed surfaces and `APP_SURFACES` is `'hidden'`.
                  */
                  <li key={category.slug}>
                    <span className="inline-flex items-center rounded-full bg-muted px-3 py-1.5 type-body-sm font-medium text-muted-foreground">
                      {t(category.labelKey)}
                    </span>
                  </li>
                ),
              )}
              {showAppSurfaces() ? (
                <li>
                  <Link
                    to={routes.categories}
                    className="touch-target inline-flex items-center rounded-full px-3.5 type-body-sm font-semibold text-primary transition-colors duration-hover ease-out hover:underline"
                  >
                    {t('home.hero.categories.all')}
                  </Link>
                </li>
              ) : null}
            </ul>

            <div className="hero-mt-cta flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
              {/* "Muted", as the client drew it — but a SOLID muted fill, not
                  the `secondary` default. Left as-is it is a bordered white
                  button, which is the same object as the outlined pill beside
                  it, and two CTAs that look identical are one CTA. */}
              <Link
                to={routes.download}
                className={buttonClasses(
                  'secondary',
                  'lg',
                  'hero-cta w-full border-transparent bg-muted text-foreground hover:bg-border sm:w-auto',
                )}
              >
                {t('home.hero.cta.earlyAccess')}
                <span aria-hidden="true">→</span>
              </Link>
              <Link
                to={routes.howItWorks}
                className={buttonClasses('outline', 'lg', 'w-full rounded-full sm:w-auto')}
              >
                {t('home.hero.cta.howItWorks')}
              </Link>
            </div>

            {/* Renders "Coming soon to App Store / Google Play" while
                `lib/appStores.ts` holds a null href — the client's own
                "Coming to the App Store · Google Play", and accurate until the
                listings exist. */}
            <AppStoreBadges className="hero-mt-wide justify-center lg:justify-start" />

            <div className="hero-mt-wide flex flex-col items-center gap-1 lg:items-start">
              {stats.isError ? null : stats.isPending ? (
                <Skeleton className="h-5 w-40" />
              ) : (
                /*
                  `rating_average` is NULL exactly when nothing has been
                  reviewed — the migration returns null when `sum(rating_count)`
                  is zero, and `reviews` IS that sum — so passing 0 as the count
                  in the null case is the same condition stated twice rather
                  than a guess, and it routes `Rating` to its "not rated yet"
                  branch.
                */
                <Rating
                  value={stats.data.ratingAverage ?? 0}
                  count={stats.data.ratingAverage === null ? 0 : stats.data.reviews}
                  size="sm"
                />
              )}
              {/* The caption belongs directly under the marks it explains —
                  the tagline is a separate claim and goes last. */}
              {stats.isError ? null : (
                <p className="type-caption text-muted-foreground">
                  {t('home.hero.trust.ratingLabel')}
                </p>
              )}
              <p className="type-body-sm text-muted-foreground">{t('home.hero.trust.tagline')}</p>
            </div>
          </div>

          {/* ── The product ──────────────────────────────────────────────────
              TWO screenshot slots: the business search in front and a request
              with its offers behind it. Both are REAL files
              (`app-businesses.png`, `app-requests.png`) and the pair is chosen
              to say the two halves of the marketplace in one picture — you can
              go looking, or you can let the offers come to you.

              ⚠️ IT USED TO BE THE DISCOVER SHOT IN FRONT, AND THAT WAS
              DELIBERATELY CHANGED. The client's § 5.3 asks for the "Screenshot
              coming soon" treatment on Discover *until its UI is finished*, so
              `discover` in `../appShots.ts` is now `file: null`. Leaving it
              here would have put the waiting panel in the most prominent image
              on the site, in the same review where he asked for the hero to be
              "considerably more attractive". If Discover's screenshot is ever
              restored, this is the decision to revisit — not a bug.

              ⚠️ THE PHONES ARE `md`-AND-UP ONLY, AND THE CARDS ARE NOT.
              On a phone the two device frames are ~500 px of hero to scroll
              past before anything actionable — to show someone a picture of a
              phone while they are holding one. The two cards say something, so
              they stay at every width: below `md` they are a plain stacked
              pair, from `md` they become overlays on the panel. One set of
              markup either way, because rendering them twice and hiding one
              copy reads the text twice to a screen reader. */}
          <div className="enter w-full">
            <div className="hero-panel relative mx-auto w-full max-w-sm md:rounded-3xl md:bg-muted md:p-6 lg:p-8">
              <div
                className="hidden items-end justify-center md:flex"
                role="img"
                aria-label={t('home.hero.showcaseLabel')}
              >
                {/* `hero-shot` carries the width. ⚠️ BOTH `max-w-none` AND
                    `lg:max-w-none` are needed: `AppShot`'s own base classes
                    include `lg:max-w-[16rem]`, and tailwind-merge treats a
                    breakpoint variant as a different group — clearing only
                    the base one silently capped these at 256 px, which is
                    exactly what the browser measured before this line. */}
                <AppShot
                  id="businesses"
                  priority
                  className="hero-shot mx-0 max-w-none lg:max-w-none"
                />
                <AppShot
                  id="requests"
                  className="hero-shot hero-shot-overlap mx-0 max-w-none translate-y-8 lg:max-w-none"
                />
              </div>

              <ul className="flex flex-col gap-3 md:pointer-events-none md:absolute md:inset-0">
                {CARDS.map((card, index) => (
                  <li
                    key={card}
                    className={
                      index === 0
                        ? 'hero-card rounded-xl border border-border bg-card p-4 shadow-2 md:absolute md:-left-4 md:top-10 md:border-transparent'
                        : 'hero-card rounded-xl border border-border bg-card p-4 shadow-2 md:absolute md:-right-4 md:bottom-8 md:border-transparent'
                    }
                  >
                    <p className="flex items-center gap-2 type-h4 text-foreground">
                      {index === 0 ? (
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full bg-success"
                        />
                      ) : null}
                      {t(`home.hero.trust.${card}.title`)}
                    </p>
                    <p className="mt-1 type-body-sm text-muted-foreground">
                      {t(`home.hero.trust.${card}.body`)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Container>

      {/* The affordance. Decoration, not a control: it says nothing a screen
          reader needs and it navigates nowhere, so it is `aria-hidden` and has
          no text — which also keeps it clear of the eager/lazy catalogue trap
          that shipped raw `HOMESECTIONS.SHOT.PENDING` to the live site. A
          button here would have to scroll the page programmatically, and
          `lib/smooth-scroll.ts` (Lenis) fights a raw `window.scrollTo`. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-5 hidden justify-center text-muted-foreground md:flex"
      >
        <span className="scroll-cue" />
      </span>
    </section>
  )
}
